import React, { ReactNode, forwardRef, useState } from 'react'
import { Box, Typography, useTheme, Slide, IconButton } from '@mui/material'
import { TransitionProps } from '@mui/material/transitions'

import BaseModal from './BaseModal'

const SlideTransition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement
  },
  ref: React.Ref<unknown>,
) {
  const direction = 'left'
  return <Slide direction={direction} ref={ref} {...props} />
})

interface SlideInModalProps {
  title?: string | ReactNode
  icon?: ReactNode
  onClose: () => void
  children: ReactNode
  width?: string
  fullScreen?: boolean
  actionButton?: ReactNode
  hideHeader?: boolean
  noPadding?: boolean
}

const SlideInModal: React.FC<SlideInModalProps> = ({
  title,
  icon,
  onClose,
  children,
  width = '500px',
  fullScreen = false,
  actionButton,
  hideHeader = false,
  noPadding = false
}) => {
  const theme = useTheme()
  const [open, setOpen] = useState(true)
  const resolvedIcon = icon ?? (title ? <i className="fa-regular fa-circle-info" /> : null)

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      transitionComponent={SlideTransition}
      fullScreen={fullScreen}
      transitionProps={{
        timeout: {
          enter: 225,
          exit: 400,
        },
        onExited: onClose
      }}
      paperSx={{
        width: fullScreen ? '100%' : width,
        maxWidth: fullScreen ? '100%' : width,
        height: '100vh',
        maxHeight: '100vh !important',
        position: 'fixed',
        right: 0,
        top: 0,
        m: 0,
        borderRadius: 0,
        boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden'
      }}
      sx={{
        '& .MuiDialog-container': {
          justifyContent: fullScreen ? 'center' : 'flex-end',
          alignItems: 'flex-start'
        },
        '& .MuiDialog-paper': {
          maxHeight: 'none !important',
        }
      }}
    >
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden'
      }}>
        {!hideHeader && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: theme.spacing(2, 3),
            position: 'relative',
          }}>
            <Box sx={{
              position: 'absolute',
              bottom: 0,
              left: theme.spacing(3), 
              right: theme.spacing(3),
              height: '1px',
              backgroundColor: '#F7F7F7',
            }} />
            <Box sx={{
              display: 'flex',
              alignItems: 'center', 
              gap: 2
            }}>
              {resolvedIcon && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  backgroundColor: '#F9FAFB',
                  color: theme.palette.primary.main,
                  fontSize: 14
                }}>
                  {resolvedIcon}
                </Box>
              )}
              {typeof title === 'string' ? (
                <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 500 }}>
                  {title}
                </Typography>
              ) : (
                title
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {actionButton}
              <IconButton
                aria-label="Fermer"
                onClick={handleClose}
                size="small"
                sx={{
                  borderRadius: '50%',
                  cursor: 'pointer',
                  height: 32,
                  padding: 0,
                  width: 32,
                  '& i': {
                    lineHeight: 1,
                    pointerEvents: 'none'
                  }
                }}
              >
                <i aria-hidden="true" className="fa-light fa-xmark" style={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          </Box>
        )}

        <Box sx={{
          flex: 1,
          overflow: 'auto',
          padding: noPadding ? 0 : theme.spacing(3),
          display: 'flex',
          flexDirection: 'column',
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}>
          {children}
        </Box>
      </Box>
    </BaseModal>
  )
}

export default SlideInModal
